import { useAppSelector } from "../custom-hooks/redux/redux";

export const thousandSeparatorType = "it-CH";

const applyThousandSeparator = (userPreference: any, numberToConvert: number) => {
  const thousandSeparator = "_";
  const decimalSeparator = ",";

  const numberWithoutDecimals = Math.floor(numberToConvert);
  const decimalPart = Math.floor((Number(numberToConvert.toFixed(2)) % 1) * 100);
  const resultdecimalPart = String(decimalPart === 0 ? "00" : decimalPart);

  // Determine the thousands separator character used in "it-CH" locale
  const thousandsSeparator = new Intl.NumberFormat("it-CH").format(1000)[1];

  const replacedWithThousandSeparators = numberWithoutDecimals
    .toLocaleString("it-CH")
    .replace(new RegExp(thousandsSeparator, "g"), thousandSeparator);

  return `${replacedWithThousandSeparators}${decimalSeparator}${resultdecimalPart}`;
};

const useThousandSeparatorFunction = (): Function => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  return (number: number) => applyThousandSeparator(currentUserAuthorizations, number);
};

export default useThousandSeparatorFunction;

// logic for Number new method

export const functionToAddThousandSeparators = (userPreferences: any) => {
  // adds new method to Number prototype, that will convert number to string with thousand separators and decimal separator according to locale
  // decimals is the parameter that says how much decimals should be after the decimal separator, by default is 2, pass 'allDigits' to display origin number with all decimals not rounded
  Number.prototype.toLocaleCustom = function createApplyThousandSeparator(decimals: number | "allDigits" = 2): string {
    const numberToConvert = this as number;

    if (!userPreferences) {
      const userLocale = thousandSeparatorType || navigator.language;

      if (decimals === "allDigits") {
        const integer = numberToConvert < 0 ? Math.ceil(numberToConvert) : Math.floor(numberToConvert);

        const convertedToLocale = new Intl.NumberFormat(userLocale).format(1000.25);

        const decimalSeparator = convertedToLocale[convertedToLocale.length - 3];

        const formatted = numberToConvert.toLocaleString(userLocale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 20,
        });

        const splited = formatted.split(decimalSeparator);

        const decimalPart = !!splited[1] && splited[1] !== "0" ? splited[1] : 0;

        return decimalPart === 0
          ? `${integer.toLocaleString(userLocale)}`
          : `${integer.toLocaleString(userLocale)}${decimalSeparator}${decimalPart}`;
      }

      return numberToConvert.toLocaleString(userLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    if (decimals === "allDigits") {
      // extract user preferences for decimals and thousand separators from store
      const thousandSeparator = " ";
      const decimalSeparator = ".";

      const numberWithoutDecimals = numberToConvert < 0 ? Math.ceil(numberToConvert) : Math.floor(numberToConvert);
      const decimalPart =
        numberToConvert < 0
          ? numberToConvert * -1 - numberWithoutDecimals * -1
          : numberToConvert - numberWithoutDecimals;

      // Determine the thousands separator character used in "thousandSeparatorType" locale
      const thousandsSeparator = new Intl.NumberFormat(thousandSeparatorType).format(1000)[1];

      const replacedWithThousandSeparators = numberWithoutDecimals
        .toLocaleString(thousandSeparatorType)
        .replace(new RegExp(thousandsSeparator, "g"), thousandSeparator);

      return decimalPart === 0
        ? `${replacedWithThousandSeparators}`
        : `${replacedWithThousandSeparators}${decimalSeparator}${decimalPart.toString().slice(2)}`;
    } else {
      const thousandSeparator = " ";
      const decimalSeparator = ".";

      const numberWithoutDecimals = numberToConvert < 0 ? Math.ceil(numberToConvert) : Math.floor(numberToConvert);
      const decimalPart = Math.floor(
        (Number((numberToConvert < 0 ? -numberToConvert : numberToConvert).toFixed(decimals)) % 1) *
          Math.pow(10, decimals)
      );
      const resultdecimalPart = String(decimalPart === 0 ? "0".repeat(decimals) : decimalPart);

      // Determine the thousands separator character used in "thousandSeparatorType" locale
      const thousandsSeparator = new Intl.NumberFormat(thousandSeparatorType).format(1000)[1];

      const replacedWithThousandSeparators = numberWithoutDecimals
        .toLocaleString(thousandSeparatorType)
        .replace(new RegExp(thousandsSeparator, "g"), thousandSeparator);

      return decimals === 0
        ? `${replacedWithThousandSeparators}`
        : `${replacedWithThousandSeparators}${decimalSeparator}${resultdecimalPart}`;
    }
  };

  // adds new method to String prototype, that will convert string to number according to the thousand separators and decimal separator
  // decimals is the parameter that says how much decimals should be after the decimal separator
  String.prototype.toNumberFromLocaleCustom = function createApplyThousandSeparator(decimals?: number): number {
    const numberToConvert = this as string;

    if (!userPreferences) {
      const userLocale = thousandSeparatorType || navigator.language;

      // Determine the thousands separator character used in user locale
      const convertedToLocale = new Intl.NumberFormat(userLocale).format(1000.25);
      const thousandsSeparator = convertedToLocale[1];
      const decimalSeparator = convertedToLocale[convertedToLocale.length - 3];

      const splitedNumberToConvertByDecimalSeparator = numberToConvert.split(decimalSeparator);

      const integerPart = splitedNumberToConvertByDecimalSeparator[0].split(thousandsSeparator).join("");

      const decimalPart = splitedNumberToConvertByDecimalSeparator[1]?.split(thousandsSeparator)?.join("") ?? "0";

      const decimalPartResult = !decimals
        ? `0.${decimalPart}`
        : decimals > decimalPart.length
        ? `0.${decimalPart}${"0".repeat(decimals - decimalPart.length)}`
        : `0.${decimalPart.substring(0, decimals)}`;

      const resultNumber = Number.isNaN(Number(integerPart))
        ? -1 * Number(decimalPartResult)
        : Number(integerPart) < 0 || integerPart[0] === "-"
        ? -(Number(-integerPart) + Number(decimalPartResult))
        : Number(integerPart) + Number(decimalPartResult);

      return resultNumber;
    } else {
      // extract thousands separator and decimal separator from userPreferences when backend will be ready and replace later
      const thousandsSeparator = "'";
      const decimalSeparator = ".";

      const splitedNumberToConvertByDecimalSeparator = numberToConvert.split(decimalSeparator);

      const integerPart = splitedNumberToConvertByDecimalSeparator[0].split(thousandsSeparator).join("");

      const decimalPart = splitedNumberToConvertByDecimalSeparator[1]?.split(thousandsSeparator)?.join("") ?? "0";

      const decimalPartResult = !decimals
        ? `0.${decimalPart}`
        : decimals > decimalPart.length
        ? `0.${decimalPart}${"0".repeat(decimals - decimalPart.length)}`
        : `0.${decimalPart.substring(0, decimals)}`;

      const resultNumber = Number.isNaN(Number(integerPart))
        ? -1 * Number(decimalPartResult)
        : Number(integerPart) < 0
        ? -(Number(-integerPart) + Number(decimalPartResult))
        : Number(integerPart) + Number(decimalPartResult);

      return resultNumber;
    }
  };
};
